import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { needById, needs } from '../../data/needs';
import { brand } from '../../data/palette';
import { NeedDetail } from '../needs/NeedDetail';
import { NeedsOrbit } from '../needs/NeedsOrbit';
import { NeedsRibbon } from '../needs/NeedsRibbon';
import { Advance } from '../ui/Advance';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
import type { NeedId } from '../../types/filtek';
interface NeedsStageProps {
  layout: 'ribbon' | 'orbit';
}

/** The family introduced through clinical need — never as a wall of packaging. */
export function NeedsStage({
  layout
}: NeedsStageProps) {
  const {
    signals,
    push,
    next,
    direction,
    primaryNeed
  } = useExperience();
  const [selected, setSelected] = useState<NeedId | undefined>();
  const active = selected ? needById(selected) : undefined;
  const select = (id: NeedId) => {
    setSelected(id === selected ? undefined : id);
    push('exploredNeeds', id);
  };
  const exploredCount = signals.exploredNeeds.length;
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="needs-title" data-id="element-283" data-mp="pf6vuif3z1xe">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-284" data-mp="kj82shiu4vqn">
        <div data-id="element-285" data-mp="fjvdzr8elelf">
          <Eyebrow accent={active?.accent ?? brand.green} data-id="element-286" data-mp="fkeynfpfuh35">
            Three members · one cousin
          </Eyebrow>
          <StageTitle id="needs-title" size="display-sm" className="mt-6" data-id="element-287" data-mp="186q16vwlus2l">
            When you need…
          </StageTitle>
        </div>
        <p className="max-w-xs text-sm font-light leading-relaxed text-mute" data-id="element-288" data-mp="13ey7wdm2e88r">
          Choose a demand and the family shows you who answers it.
        </p>
      </div>

      <div className="mt-12 sm:mt-16" data-id="element-289" data-mp="1vw0tdozw3e54">
        {layout === 'orbit' ? <NeedsOrbit selected={selected} explored={signals.exploredNeeds} matched={primaryNeed} onSelect={select} data-id="element-290" data-mp="1vy55gu7xzjgh" /> : <NeedsRibbon selected={selected} explored={signals.exploredNeeds} matched={primaryNeed} onSelect={select} data-id="element-291" data-mp="3lfisw51lfbp3" />}
      </div>

      <div className="mt-12 sm:mt-14" data-id="element-292" data-mp="3bligrj7w966v">
        <AnimatePresence mode="wait" data-id="element-293" data-mp="3gm64nw0tt6wv">
          {active ? <NeedDetail key={active.id} need={active} data-id="element-294" data-mp="3gkmnm37ciru1" /> : <motion.p key="placeholder" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="max-w-md text-base font-light leading-relaxed text-mute" data-id="element-295" data-mp="32m51g9x39ne8">
              Four kinds of demand. Three members and a cousin. The solution appears once you have
              named the demand — not before.
            </motion.p>}
        </AnimatePresence>
      </div>

      {exploredCount > 0 && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }} className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-enamel/10 pt-8" data-id="element-296" data-mp="vvp7vazg7lwd">
          <p className="label-mono text-[10px] text-mute" data-id="element-297" data-mp="2q5voccf7gy18">
            {exploredCount} of {needs.length} explored
          </p>
          <Advance label={exploredCount < 2 ? 'Or move on' : 'Continue'} onClick={next} accent={active?.accent ?? brand.green} data-id="element-298" data-mp="2lbrglqy48lz3" />
        </motion.div>}
    </Stage>;
}